import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import * as yup from 'yup';
import { statusColors } from 'utils/variables/statusColors';
import { DatesHelper } from 'utils/classes/DatesHelper';

/* -------------------------------------------------------------------------- */
// #region HELPER COMPONENTS
/* -------------------------------------------------------------------------- */

const HeaderWithTitle = ({ header, title, description }) => {
  return (
    <div
      title={`${title} - ${description}`}
      style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}
    >
      {header}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
// #region SCHEMAS DE VALIDAÇÃO (YUP)
/* -------------------------------------------------------------------------- */

const pauseSchema = yup.object().shape({
  id: yup.number().required(),
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  active: yup.boolean().nullable(),
  time_alert: yup.number().nullable()
});

const queueSimpleSchema = yup.object().shape({
  id: yup.number().nullable(),
  name: yup.string().required()
});

const rawAgentSchema = yup.object().shape({
  agentName: yup.string().nullable(),
  agentLogin: yup.string().nullable(),
  status: yup.string().nullable(),
  pauseId: yup.number().nullable(),
  occupationStatus: yup.string().nullable(),
  dateOfLogin: yup.string().nullable(),
  dateOfPause: yup.string().nullable(),
  chatsAssigned: yup.number().nullable(),
  chatsInAttendance: yup.number().nullable(),
  finishedChats: yup.number().nullable(),
  queues: yup.array().of(queueSimpleSchema).nullable(),
  queueId: yup.number().nullable(),
  queueName: yup.string().nullable()
});

const contextSchema = yup.object().shape({
  activeQueueNames: yup.array().of(yup.string()).required(),
  activeAgentLogins: yup.array().of(yup.string()).nullable(),
  pauses: yup.array().of(pauseSchema).nullable()
});

const formattedAgentSchema = yup.object().shape({
  agentName: yup.string().required(),
  agentLogin: yup.string().required(),
  queueTimestamp: yup.string().nullable(),
  attributed: yup.string().required(),
  active: yup.string().required(),
  closed: yup.string().required(),
  backgroundColor: yup.string().required(),
  status: yup.string().required(),
  occupationStatus: yup.string().nullable(),
  pauseName: yup.string().required(),
  pauseDescription: yup.string().nullable(),
  pauseTimeAlert: yup.number().nullable(),
  pauseData: yup.object().nullable(),
  queues: yup.array().of(queueSimpleSchema).required(),
  queueId: yup.number().nullable(),
  queueName: yup.string().nullable()
});

/* -------------------------------------------------------------------------- */
// #region FUNÇÕES AUXILIARES
/* -------------------------------------------------------------------------- */

/** @typedef {'one_card_per_agent' | 'one_card_per_agent_per_queue' | 'one_card_per_queue'} LayoutOptions */
export const layoutToMonitoringChatQueuesSocketGroup = (layout) => {
  if (layout === 'one_card_per_agent') return 'agent';
  if (layout === 'one_card_per_agent_per_queue') return 'agent_queue';
  if (layout === 'one_card_per_queue') return 'queue';
  return 'agent';
};

const removeDuplicates = (items, keyFn) => {
  const seen = new Map();
  items.forEach((item) => {
    try {
      const key = keyFn(item);
      if (!seen.has(key)) {
        seen.set(key, item);
      }
    } catch (e) {
      console.warn('Erro ao gerar chave para desduplicação', item);
    }
  });
  return Array.from(seen.values());
};

const getAgentStatusInfo = (agent, pauses) => {
  let pauseData = null;
  let backgroundColor = null;

  if (
    (agent.status === 'isPaused' || agent.status === 'isActivePause') &&
    agent.pauseId
  ) {
    pauseData = pauses?.find?.((pause) => pause?.id === agent.pauseId);
    backgroundColor = pauseData?.active
      ? statusColors.ACTIVE_PAUSE
      : statusColors.PAUSE;
  } else if (agent.occupationStatus) {
    const backgroundColorMap = {
      fullyOccupied: statusColors.FULLY_OCCUPIED,
      partiallyOccupied: statusColors.PARTIALLY_OCCUPIED,
      totallyFree: statusColors.AVAILABLE
    };
    backgroundColor = backgroundColorMap[agent.occupationStatus];
  } else if (agent.status === 'isAvailable') {
    backgroundColor = statusColors.AVAILABLE;
  } else {
    backgroundColor = statusColors.OFFLINE;
  }

  const pauseName =
    pauseData?.name ||
    (agent.status === 'isAvailable' ? 'Disponível' : 'Desconhecido');

  return { pauseData, backgroundColor, pauseName };
};

/* -------------------------------------------------------------------------- */
// #region LÓGICA CORE
/* -------------------------------------------------------------------------- */

const formatAgentData = (agent, context) => {
  const inputValidation = rawAgentSchema.safeValidateSync(agent, {
    abortEarly: true,
    stripUnknown: true
  });

  if (!inputValidation.success) {
    console.warn(
      'Agente ignorado (Input Inválido):',
      inputValidation.error.message,
      agent
    );
    return null;
  }

  const validAgent = inputValidation.data;
  const { pauses, activeQueueNames } = context;

  const displayQueues = (validAgent.queues || []).filter((q) =>
    activeQueueNames.includes(q.name)
  );

  const { pauseData, backgroundColor, pauseName } = getAgentStatusInfo(
    validAgent,
    pauses
  );

  const formattedData = {
    agentName: validAgent.agentName || '',
    agentLogin: validAgent.agentLogin || '',
    queueTimestamp:
      validAgent.status === 'isAvailable'
        ? validAgent.dateOfLogin
          ? DatesHelper.applyHoursToISO(validAgent.dateOfLogin, -3)
          : null
        : DatesHelper.applyHoursToISO(validAgent.dateOfPause || new Date(), -3),
    attributed: (validAgent.chatsAssigned || 0).toString(),
    active: (validAgent.chatsInAttendance || 0).toString(),
    closed: (validAgent.finishedChats || 0).toString(),
    backgroundColor: backgroundColor,
    status: validAgent.status || 'unknown',
    occupationStatus: validAgent.occupationStatus || null,
    pauseName: pauseName,
    pauseDescription: pauseData?.description || '',
    pauseTimeAlert: pauseData?.time_alert || 0,
    pauseData: pauseData || null,
    queues: displayQueues,
    ...(validAgent.queueId && { queueId: validAgent.queueId }),
    ...(validAgent.queueName && { queueName: validAgent.queueName })
  };

  const outputValidation = formattedAgentSchema.safeValidateSync(formattedData);

  if (!outputValidation.success) {
    console.warn(
      'Agente ignorado (Output Inválido):',
      outputValidation.error.message
    );
    return null;
  }

  return outputValidation.data;
};

const processQueueMode = (data, { activeQueueNames, activeAgentLogins }) => {
  if (!Array.isArray(data)) return [];

  const hasAgentFilter = activeAgentLogins && activeAgentLogins.length > 0;

  const filtered = data
    .filter((item) => {
      return item && item.name && activeQueueNames.includes(item.name);
    })
    .map((item) => {
      if (!hasAgentFilter || !Array.isArray(item.agents)) return item;
      return {
        ...item,
        agents: item.agents.filter((a) =>
          activeAgentLogins.includes(a.login)
        )
      };
    });

  return removeDuplicates(filtered, (item) => item.id || item.name);
};

const processAgentQueueMode = (data, context) => {
  if (!Array.isArray(data)) return [];

  const { activeQueueNames, activeAgentLogins } = context;
  const hasAgentFilter = activeAgentLogins && activeAgentLogins.length > 0;

  const filtered = hasAgentFilter
    ? data.filter(
        (agent) =>
          agent &&
          agent.agentLogin &&
          activeAgentLogins.includes(agent.agentLogin)
      )
    : data;

  const formatted = filtered.flatMap((agent) => {
    if (!agent || !agent.queues || !Array.isArray(agent.queues)) return [];

    const targetQueues = agent.queues.filter((q) =>
      activeQueueNames.includes(q.name)
    );

    if (targetQueues.length === 0) return [];

    return targetQueues
      .map((queueObj) => {
        const mixedAgent = {
          ...agent,
          ...queueObj,
          queues: [queueObj],
          queueId: queueObj.id,
          queueName: queueObj.name
        };

        return formatAgentData(mixedAgent, context);
      })
      .filter((agt) => Boolean(agt));
  });

  return removeDuplicates(
    formatted,
    (item) => `${item.agentLogin}-${item.queueName}`
  );
};

const processAgentMode = (data, context) => {
  if (!Array.isArray(data)) return [];

  const { activeQueueNames, activeAgentLogins } = context;
  const hasAgentFilter = activeAgentLogins && activeAgentLogins.length > 0;

  const formatted = data
    .filter((agent) => {
      if (!agent || !agent.queues) return false;
      const inQueue = agent.queues.some((q) =>
        activeQueueNames.includes(q.name)
      );
      if (!inQueue) return false;
      if (hasAgentFilter) {
        return activeAgentLogins.includes(agent.agentLogin);
      }
      return true;
    })
    .map((agent) => formatAgentData(agent, context))
    .filter((agt) => Boolean(agt) && agt.queues?.length);

  return removeDuplicates(formatted, (item) => item.agentLogin);
};

export const getProcessedMonitoringData = (
  monitoringSocketResponse,
  group,
  activeQueueNames,
  pauses,
  activeAgentLogins
) => {
  const contextValidation = contextSchema.safeValidateSync(
    { activeQueueNames, activeAgentLogins: activeAgentLogins || [], pauses },
    { stripUnknown: true }
  );

  if (!contextValidation.success) {
    console.error('Contexto inválido:', contextValidation.error);
    return [];
  }

  const validContext = contextValidation.data;
  const rawData = monitoringSocketResponse?.[group] || [];

  if (!Array.isArray(rawData)) {
    console.warn(`Dados inválidos recebidos para o grupo: ${group}`);
    return [];
  }

  switch (group) {
    case 'queue':
      return processQueueMode(rawData, validContext);
    case 'agent_queue':
      return processAgentQueueMode(rawData, validContext);
    case 'agent':
    default:
      return processAgentMode(rawData, validContext);
  }
};

/**
 * Calcula os totais de agentes por status para os filtros visuais
 */
export const getAgentsStatusCounts = (agentsList) => {
  const counts = {
    total: 0,
    available: 0,
    paused: 0,
    activePaused: 0,
    occupied: 0,
    partiallyOccupied: 0,
    totalAttributed: 0,
    totalActive: 0,
    unregistered: 0,
    offline: 0
  };

  if (!Array.isArray(agentsList)) return counts;

  const flatAgents = agentsList.flatMap((item) => {
    if (item.agents && Array.isArray(item.agents)) return item.agents;
    return item;
  });

  const uniqueAgents = removeDuplicates(
    flatAgents,
    (a) => a.agentLogin || a.login
  );

  counts.total = uniqueAgents.length;

  counts.total = uniqueAgents.length;

  flatAgents.forEach((agent) => {
    const status = agent.status;
    const attributed = Number(agent.attributed || 0);
    const active = Number(agent.active || 0);

    counts.totalAttributed += attributed;
    counts.totalActive += active;

    const occupationStatus = agent.occupationStatus;

    if (occupationStatus === 'fullyOccupied') {
      counts.occupied++;
    } else if (occupationStatus === 'partiallyOccupied') {
      counts.partiallyOccupied++;
    }

    if (status === 'isAvailable') {
      counts.available++;
    } else if (status === 'isPaused') {
      counts.paused++;
    } else if (status === 'isActivePause') {
      counts.activePaused++;
    } else if (
      status === 'UNPLACED' ||
      status === 'UNAVAILABLE' ||
      status === 'UNKNOWN'
    ) {
      counts.unregistered++;
    } else {
      counts.offline++;
    }
  });

  return counts;
};

/**
 * Filtra os agentes baseado nos filtros ativos
 */
export const getFilteredQueueMembers = (queueMemberInfo, activeFilters) => {
  if (!activeFilters || activeFilters.length === 0) return queueMemberInfo;

  return queueMemberInfo.filter((member) => {
    const status = member?.status || 'unknown';
    const occupationStatus = member?.occupationStatus;

    const isAvailable = status === 'isAvailable';
    const isPaused = status === 'isPaused';
    const isActivePause = status === 'isActivePause';
    const isOccupied = occupationStatus === 'fullyOccupied';
    const isPartiallyOccupied = occupationStatus === 'partiallyOccupied';
    const isUnregistered =
      status === 'UNPLACED' || status === 'UNAVAILABLE' || status === 'UNKNOWN';
    const isOffline =
      !isAvailable && !isPaused && !isActivePause && !isUnregistered;

    const hasInAttendance = Number(member.active || 0) > 0;
    const hasAttributed = Number(member.attributed || 0) > 0;

    if (activeFilters.includes('IS_AVAILABLE') && isAvailable) return true;
    if (activeFilters.includes('IS_PAUSED') && isPaused) return true;
    if (activeFilters.includes('IS_ACTIVE_PAUSE') && isActivePause) return true;
    if (activeFilters.includes('IS_OCCUPIED') && isOccupied) return true;
    if (activeFilters.includes('IS_PARTIALLY_OCCUPIED') && isPartiallyOccupied)
      return true;
    if (activeFilters.includes('IS_UNREGISTERED') && isUnregistered)
      return true;
    if (activeFilters.includes('IS_OFFLINE') && isOffline) return true;

    if (activeFilters.includes('HAS_IN_ATTENDANCE') && hasInAttendance)
      return true;
    if (activeFilters.includes('HAS_ATTRIBUTED') && hasAttributed) return true;

    return false;
  });
};

/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */

/**
 * Gera a configuração dos cards de estatísticas (Lado Esquerdo)
 */
export const getCardStatsConfig = (
  monitoringData,
  cardOrder,
  serviceLevelConfig
) => {
  const chatsAnswered = monitoringData.CHATS_ANSWERED || 0;
  const chatsNotAnswered = monitoringData.CHATS_NOT_ANSWERED || 0;
  const totalIncoming = chatsAnswered + chatsNotAnswered;

  const attendanceLevelValue =
    totalIncoming > 0 ? ((chatsAnswered / totalIncoming) * 100).toFixed(1) : 0;

  const acceptablePercentage = serviceLevelConfig?.acceptablePercentage ?? 80;
  const acceptablePercentageNonConformities =
    serviceLevelConfig?.acceptablePercentageOfNonConformities ?? 20;

  const serviceLevelValid = parseFloat(
    monitoringData.SERVICE_LEVEL?.percentValid || 0
  );
  const serviceLevelInvalid = parseFloat(
    monitoringData.SERVICE_LEVEL?.percentInvalid || 0
  );

  const isValidBreached = serviceLevelValid < acceptablePercentage;

  const isInvalidBreached =
    serviceLevelInvalid > acceptablePercentageNonConformities;

  const isSlaBreached = isValidBreached || isInvalidBreached;

  const alertBackgroundColor = statusColors.FULLY_OCCUPIED;

  const cardDefinitions = [
    {
      id: 'TMA',
      title: 'Tempo médio de atendimento',
      layout: 'vertical-centered-single',
      value: monitoringData.TMA || '00:00:00'
    },
    {
      id: 'TMO',
      title: 'Tempo médio operacional',
      layout: 'vertical-centered-single',
      value: monitoringData.TMO || '00:00:00'
    },
    {
      id: 'MTW',
      title: 'Maior tempo de espera',
      layout: 'vertical-centered-single',
      value: monitoringData.MOST_TIME_IN_WAIT || '00:00:00'
    },
    {
      id: 'TME',
      title: 'Tempo médio de espera',
      layout: 'vertical-centered-single',
      value: monitoringData.TME || '00:00:00'
    },
    {
      id: 'TDRD',
      title: 'Tempo de resposta após distribuição',
      layout: 'vertical-centered-single',
      value: monitoringData.TDRD || '00:00:00'
    },
    {
      id: 'TDR',
      title: 'Tempo de resposta',
      layout: 'vertical-centered-single',
      value: monitoringData.TDR || '00:00:00'
    },
    {
      id: 'CHATS_IN_WAITING',
      title: 'Fila de espera',
      layout: 'vertical-centered-single',
      value: monitoringData.CHATS_IN_WAITING || 0
    },
    {
      id: 'TM_ASSIGN',
      title: 'Tempo médio em atribuição',
      layout: 'vertical-centered-single',
      value: monitoringData.tmInAssigned || '00:00:00'
    },
    {
      id: 'ATTENDANCE_LEVEL',
      title: 'Nível de Atendimento (%)',
      layout: 'vertical-centered-single',
      value: `${attendanceLevelValue}%`,
      description: 'Atendidas / Total'
    },
    {
      id: 'SERVICE_LEVEL',
      title: 'Nível de serviço (Qtd)',
      layout: 'vertical-centered-double',
      leftValue: {
        description: 'Conforme',
        value: monitoringData.SERVICE_LEVEL?.numberOfValid ?? 0
      },
      rightValue: {
        description: 'Não conforme',
        value: monitoringData.SERVICE_LEVEL?.numberOfInvalid ?? 0
      }
    },
    {
      id: 'SERVICE_LEVEL_PERCENTAGE',
      title: 'Nível de serviço (%)',
      layout: 'vertical-centered-double',
      isAlert: isSlaBreached,
      backgroundColor: isSlaBreached ? alertBackgroundColor : undefined,
      leftValue: {
        description: 'Conforme',
        value: isNaN(monitoringData.SERVICE_LEVEL?.percentValid)
          ? 0
          : `${monitoringData.SERVICE_LEVEL?.percentValid ?? 0}%`,
        suffix: '%'
      },
      rightValue: {
        description: 'Não conforme',
        value: isNaN(monitoringData.SERVICE_LEVEL?.percentInvalid)
          ? 0
          : `${monitoringData.SERVICE_LEVEL?.percentInvalid ?? 0}%`,
        suffix: '%'
      }
    }
  ];

  return cardOrder
    .map((id) => cardDefinitions.find((c) => c.id === id))
    .filter(Boolean);
};

/**
 * Gera a configuração dos círculos do cabeçalho
 */
export const getCircleStatsConfig = (
  monitoringData,
  agentsStatusCounts,
  activeFilters = []
) => {
  return [
    {
      id: 'available_agents',
      title: 'Disponíveis',
      value: agentsStatusCounts.available,
      backgroundColor: statusColors.AVAILABLE,
      clickable: true,
      filterKey: 'IS_AVAILABLE',
      isActive: activeFilters.includes('IS_AVAILABLE')
    },
    {
      id: 'paused_agents',
      title: 'Em Pausa',
      value: agentsStatusCounts.paused,
      backgroundColor: statusColors.PAUSE,
      clickable: true,
      filterKey: 'IS_PAUSED',
      isActive: activeFilters.includes('IS_PAUSED')
    },
    {
      id: 'active_paused_agents',
      title: 'Pausa Ativa',
      value: agentsStatusCounts.activePaused,
      backgroundColor: statusColors.ACTIVE_PAUSE,
      clickable: true,
      filterKey: 'IS_ACTIVE_PAUSE',
      isActive: activeFilters.includes('IS_ACTIVE_PAUSE')
    },
    {
      id: 'occupied_agents',
      title: 'Totalmente Ocupados',
      value: agentsStatusCounts.occupied,
      backgroundColor: statusColors.FULLY_OCCUPIED,
      clickable: true,
      filterKey: 'IS_OCCUPIED',
      isActive: activeFilters.includes('IS_OCCUPIED')
    },
    // Esses filtros aqui só existem no telefonia, mas podem vir pro chat então já deixei comentado
    // {
    //   id: 'unregistered_agents',
    //   title: 'Offline: Logado mas sem fila ou registro',
    //   value: agentsStatusCounts.unregistered || 0,
    //   backgroundColor: statusColors.UNPLACED,
    //   clickable: true,
    //   filterKey: 'IS_UNREGISTERED',
    //   isActive: activeFilters.includes('IS_UNREGISTERED')
    // },
    // {
    //   id: 'offline_agents',
    //   title: 'Não logado: Sem sessão ativa na plataforma',
    //   value: agentsStatusCounts.offline || 0,
    //   backgroundColor: statusColors.OFFLINE,
    //   clickable: true,
    //   filterKey: 'IS_OFFLINE',
    //   isActive: activeFilters.includes('IS_OFFLINE')
    // },
    // Esse filtro já tinha sido implementado mas está sendo removido a pedido da Jessika
    // {
    //   id: 'partially_occupied_agents',
    //   title: 'Parcialmente Ocupados',
    //   value: agentsStatusCounts.partiallyOccupied,
    //   backgroundColor: statusColors.PARTIALLY_OCCUPIED,
    //   clickable: true,
    //   filterKey: 'IS_PARTIALLY_OCCUPIED',
    //   isActive: activeFilters.includes('IS_PARTIALLY_OCCUPIED')
    // },
    {
      id: 'chats_in_attendance',
      title: 'Em atendimento',
      value: agentsStatusCounts.totalActive,
      backgroundColor: '#2ECC71',
      clickable: true,
      filterKey: 'HAS_IN_ATTENDANCE',
      isActive: activeFilters.includes('HAS_IN_ATTENDANCE')
    },
    {
      id: 'chats_not_answered',
      title: 'Conversas não atendidas',
      value:
        agentsStatusCounts.totalAttributed +
        (monitoringData.CHATS_IN_WAITING || 0),
      backgroundColor: '#E74C3C',
      clickable: true,
      filterKey: 'HAS_ATTRIBUTED',
      isActive: activeFilters.includes('HAS_ATTRIBUTED')
    },
    {
      id: 'total_agents',
      title: 'Total de usuários logados',
      value: agentsStatusCounts.total,
      backgroundColor: '#5E72E4',
      clickable: false,
      filterKey: null,
      isActive: false
    }
  ];
};

/**
 * Definição das colunas da Matriz Horária
 */
export const matrixColumnsConfig = [
  {
    accessorKey: 'hourRange',
    header: 'Horário',
    size: 100,
    cell: (info) => {
      const val = info.getValue() || '';
      if (val.includes(' - ')) return val;
      const start = parseInt(val, 10);
      if (!isNaN(start)) {
        const end = (start + 1) % 24;
        const fmt = (n) => n.toString().padStart(2, '0');
        return `${fmt(start)}:00 - ${fmt(end)}:00`;
      }
      return val;
    },
    size: 100
  },
  {
    accessorKey: 'openChats',
    header: () => (
      <HeaderWithTitle
        header="Abertas"
        title="Total de conversas iniciadas"
        description="Total de conversas iniciadas no horário"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'chatsAttended',
    header: () => (
      <HeaderWithTitle
        header="Atendidas"
        title="Total de conversas atendidas"
        description="Total de conversas atendidas dentro das conversas iniciadas no horário"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'chatsNotAttended',
    header: () => (
      <HeaderWithTitle
        header="Não Atendidas"
        title="Conversas Não Atendidas"
        description="Dentro das conversas iniciadas no horário especifico quantas não foram atendidas?"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'closedChats',
    header: () => (
      <HeaderWithTitle
        header="Encerradas"
        title="Conversas Encerradas"
        description="Total de conversas encerradas dentro das conversas que estavam abertas"
      />
    ),
    size: 100
  },

  {
    accessorKey: 'closedByTimeout',
    header: () => (
      <HeaderWithTitle
        header="Abandonadas"
        title="Conversas Abandonadas"
        description="Conversas encerradas pela regra de ociosidade"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tme',
    header: () => (
      <HeaderWithTitle
        header="TME"
        title="Tempo médio de espera"
        description="Após a distribuição do contato, para atribuídos ou fila de espera, quanto tempo leva até que o atendente clique para atender?"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tma',
    header: () => (
      <HeaderWithTitle
        header="TMA"
        title="Tempo médio de atendimento"
        description="Quanto tempo em média o atendente passa em atendimento por conversa?"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tmo',
    header: () => (
      <HeaderWithTitle
        header="TMO"
        title="Tempo médio Operacional"
        description="Tempo médio da abertura da conversa até o encerramento"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tta',
    header: () => (
      <HeaderWithTitle
        header="TTA"
        title="Tempo total de atendimento"
        description="Tempo total de atendimento das conversas em aberto, criadas somente no intervalo do horário. Conversas encerradas ainda contam."
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tmfr',
    header: () => (
      <HeaderWithTitle
        header="1ª Resp"
        title="Tempo médio de primeira resposta"
        description="Após o atendimento da conversa, quanto tempo o atendente leva para responder a primeira mensagem do cliente?"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tmr',
    header: () => (
      <HeaderWithTitle
        header="T.M.R"
        title="Tempo médio de resposta"
        description="Quanto tempo o atendente levou para responder cada mensagem dos clientes, em média?"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'tmInAssigned',
    header: () => (
      <HeaderWithTitle
        header="TM Atrib."
        title="Tempo médio em atribuição"
        description="Quanto tempo em média o atendente leva para atender uma conversa que esta em atribuição?"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'serviceLevel',
    header: () => (
      <HeaderWithTitle
        header="N. Serviço"
        title="Nível de Serviço"
        description="Porcentagem de conversas atendidas em um tempo menor ou igual ao definido como conforme nas regras de nível de serviço para chat"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'attendanceLevel',
    header: () => (
      <HeaderWithTitle
        header="N. Atend."
        title="Nível de Atendimento"
        description="Porcentagem de conversas atendidas dentro do número de conversas iniciadas especificamente naquele período, nao do total"
      />
    ),
    size: 100
  },
  {
    accessorKey: 'projectedVolume',
    header: () => (
      <HeaderWithTitle
        header="Vol. Projetado"
        title="Volume Projetado"
        description="Volume projetado para este horário"
      />
    ),
    size: 100,
    cell: (info) => info.getValue()?.projectedVolume || 0
  },
  {
    accessorKey: 'chatsCount',
    header: () => (
      <HeaderWithTitle
        header="Volume Total"
        title="Volume Total"
        description="Conversas que estavam abertas no momento"
      />
    ),
    size: 100
  }
];

export const initialMatrixColumnOrder = matrixColumnsConfig.map(
  (c) => c.id ?? c.accessorKey
);
